import * as React from 'react';
import UnderwriterNotes from '../UnderwriterNotes';
import { Grid, Typography } from '@mui/material';
import { Spacing } from 'components/Layout';
import { useSelector } from 'react-redux';
import {
    getSovLocations,
    getSubmissionDetails,
    getSubmissionReadOnly,
    getUwBRPropertyValues,
    getUwBRReview,
    getUwBRSubscribers,
    getUwReviewsData,
    getUwReviewSubjectivities,
} from 'application/redux/selectors';
import { UWReviewTabAPI } from '../StandardReview';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { UnderwriterReview, UwBRLocationReview, UwReview } from 'types/underwriting.type';
import { isLocationAddEndorsement } from 'helpers/endorsements';
import { PRODUCT_LINE_TYPES, STATUS_TYPES } from 'constants/index';
import { getProductFilteredLocations, isInUnderwriting } from 'helpers/submissions';
import { Location } from 'types/location.type';
import ProjectDescription from './ProjectDescription';
import { TopLevelReviewCard } from './TopLevelReview';
import BrLocationReviewGrid from './Property/BrLocationReviewGrid';
import {
    getBrTotalPolicyPremiumFromLocations,
    getBrTotalPolicyPremiumFromLocationsWithParticipation,
    getReviewLumenParticipationPercent,
    getBrTotalProgramPremiumFromLocationsWithParticipation,
    getBrPremiumSubtotal,
} from 'helpers/brCalculationHelper';
import { useActions } from 'hooks/useActions';
import * as Actions from 'application/redux/actions';
import { unformatNumberString } from 'helpers/formatters';
import LrButton from 'components/LrButton';
import { postUWReview } from 'services/uwReview';
import LrToastNotify from 'components/LrToastNotify';
import {
    validateBrUnderwriterReview,
    validateLocationReview,
    getCombinedSubscriberCapacity,
} from './Helpers/validations';
import { deepCopy } from '../../../../../../helpers/object';
import BRPropertyModel from './Property/BRPropertyModel';
import { UwSummarySubjectivity } from 'application/Models/UwSummarySubjectivity';
import { DEFAULT_BR_SUBJECTIVITIES } from './subjectivities';

interface Props {
    history: any;
    selectedUnderwriterReviews?: UnderwriterReview[];
    onReviewValid?: (productLineType: string, isValid: boolean, hasLocations: boolean) => void;
    inForce?: boolean;
    readOnly: boolean;
    showSaveDraftButton?: boolean;
    onSave?: () => void;
}

const MY_PRODUCT_LINE_TYPE: string = PRODUCT_LINE_TYPES.BUILDERS_RISK;

const BuildersRiskStandardReview: React.RefForwardingComponent<UWReviewTabAPI, Props> = (props, ref) => {
    const { selectedUnderwriterReviews, onReviewValid, readOnly, inForce, showSaveDraftButton = true, onSave } = props;

    // global state
    const submission = useSelector(getSubmissionDetails);
    const propertyValues = useSelector(getUwBRPropertyValues);
    const uwReviews = useSelector(getUwReviewsData);
    const uwBrReview = useSelector(getUwBRReview);
    const uwSubjectivities = useSelector(getUwReviewSubjectivities);
    const subscribers = useSelector(getUwBRSubscribers);
    const locations = useSelector(getSovLocations);
    const globalReadonly = useSelector(getSubmissionReadOnly);
    const {
        uwReviewSaveBRReview,
        uwReviewSaveAllBRProperties,
        uwReviewsFetchData,
        uwReviewSaveAllSubjectivities,
        uwReviewSaveBRSubscription,
    } = useActions(Actions);

    // local state
    const [productFilteredLocations, setProductFilteredLocations] = useState<Location[]>([]);
    const [saveDraftRunning, setSaveDraftRunning] = useState(false);
    const [saveDraftConfirmation, setSaveDraftConfirmation] = useState('');
    const [isReviewValid, setIsReviewValid] = useState<boolean>(false);
    const [formErrorText, setFormErrorText] = useState<string>('');
    const [isShowingErrorText, setIsShowingErrorText] = useState<boolean>(false);

    const selectedUnderwriterReviewId = selectedUnderwriterReviews
        ? selectedUnderwriterReviews?.find((uw) => uw.productLineTypeCode === MY_PRODUCT_LINE_TYPE)?.id || null
        : undefined;
    const isLocationAddEndorsementBool = isLocationAddEndorsement(submission);

    const defaultUwBrReview: Partial<UnderwriterReview> = {
        isLeader: false,
        policyLimitOfLiability: unformatNumberString(submission.totalInsurableValue || ''),
    };

    // Setup default review or load an existing one
    const handleSelectedReviewUpdate = () => {
        if (inForce) {
            const currentUwReview = uwReviews.find(
                (uwr) =>
                    uwr.underwriterReview.productLineTypeCode === MY_PRODUCT_LINE_TYPE && uwr.underwriterReview.inForce
            );
            const allLocationReviews = currentUwReview?.locationReviews;

            const allSubjectivities: UwSummarySubjectivity[] = uwReviews.flatMap((uwr) => {
                if (uwr.underwriterReview?.subjectivities) {
                    return uwr.underwriterReview?.subjectivities?.map((subjectivity) => ({
                        submissionId: uwr.underwriterReview.submissionId.toString(),
                        subjectivityText: subjectivity.subjectivityText,
                    }));
                } else {
                    return [];
                }
            });

            uwReviewSaveBRReview(currentUwReview?.underwriterReview);
            uwReviewSaveAllSubjectivities(allSubjectivities);
            uwReviewSaveAllBRProperties(allLocationReviews);
            if (currentUwReview?.underwriterReview?.subscription?.subscribers) {
                uwReviewSaveBRSubscription(currentUwReview.underwriterReview.subscription.subscribers);
            }
            return;
        }

        const newReview = uwReviews.find((uwr) => uwr.underwriterReview.id === selectedUnderwriterReviewId);
        const tmpReadonly = selectedUnderwriterReviewId !== undefined || readOnly || globalReadonly;

        if (!newReview) {
            const firstUwReview = getExistingReview(readOnly);
            const usingProperties: BRPropertyModel[] = [
                ...(firstUwReview?.locationReviews.filter(
                    (locationReview: UwBRLocationReview) =>
                        !locations.find((loc) => loc.id === locationReview.locationId)!.markedAsDeleted
                ) || []),
            ];

            // Seed subjectivities: prefer from most recent BR review if present, else defaults
            if (firstUwReview?.underwriterReview?.subjectivities?.length) {
                const subjectivities: UwSummarySubjectivity[] = firstUwReview.underwriterReview.subjectivities.map(
                    (s) => ({
                        ...s,
                        submissionId: (firstUwReview?.underwriterReview.submissionId || submission.id).toString(),
                    })
                );
                uwReviewSaveAllSubjectivities(subjectivities);
            } else if (!firstUwReview?.underwriterReview) {
                const defaultSubs: UwSummarySubjectivity[] = DEFAULT_BR_SUBJECTIVITIES.map((s) => ({
                    subjectivityText: s,
                    submissionId: submission.id,
                }));
                uwReviewSaveAllSubjectivities(defaultSubs);
            }

            uwReviewSaveBRReview(firstUwReview?.underwriterReview || defaultUwBrReview);
            uwReviewSaveAllBRProperties(usingProperties);
            if (firstUwReview?.underwriterReview?.subscription?.subscribers) {
                uwReviewSaveBRSubscription(firstUwReview.underwriterReview.subscription.subscribers);
            }
            // uwReviewSaveBRReview(defaultUwBrReview);
        } else {
            seedFoundData(newReview, tmpReadonly);
            if (newReview.underwriterReview?.id !== uwBrReview?.id) {
                // Only initialize with the found review if it's different than what is already loaded.
                // Otherwise we might overwrite some changes we've made and saved to state.
                uwReviewSaveBRReview(newReview.underwriterReview);
            }
            uwReviewSaveAllBRProperties([...newReview.locationReviews]);
            // Seed subjectivities from the selected review
            if (newReview.underwriterReview?.subjectivities) {
                const subjectivities: UwSummarySubjectivity[] = newReview.underwriterReview.subjectivities.map((s) => ({
                    ...s,
                    submissionId: newReview.underwriterReview.submissionId.toString(),
                }));
                uwReviewSaveAllSubjectivities(subjectivities);
            }
            // Seed subscribers from the selected review's subscription
            if (newReview.underwriterReview?.subscription?.subscribers) {
                uwReviewSaveBRSubscription(newReview.underwriterReview.subscription.subscribers);
            }
        }
        // setEffectiveReadOnly(tmpReadonly);
    };

    // Setup a review in global state
    useEffect(handleSelectedReviewUpdate, [selectedUnderwriterReviewId, uwReviews]);

    // Setup locations to be reviewed
    useEffect(() => {
        setProductFilteredLocations(getProductFilteredLocations(MY_PRODUCT_LINE_TYPE, locations, submission));
    }, [locations]);

    const buildReviewPayload = () => {
        const locationReviews = getPropertiesForReview();
        if (locationReviews.length > 0) {
            // All TRIA percentages should be the same (they are updated automatically by the UI to match each other).
            const exampleTriaPercentage = locationReviews[0].triaPercentage;

            const lumenParticipationStr = getReviewLumenParticipationPercent(
                unformatNumberString(uwBrReview.policyLimitOfLiability || 0),
                unformatNumberString(uwBrReview.lumenLimitOfLiability || 0)
            );
            const lumenParticipationNum = Number(lumenParticipationStr);

            // Total premium WITH TRIA for payload (includes participation adjustment)
            const totalPremWithParticipationAndTria =
                uwBrReview.policyLimitOfLiability && uwBrReview.lumenLimitOfLiability
                    ? getBrTotalPolicyPremiumFromLocationsWithParticipation(locationReviews, lumenParticipationNum) || 0
                    : null;

            return {
                referenceUWId: uwBrReview.id,
                isLeader: uwBrReview.isLeader,
                primaryLimit: uwBrReview.primaryLimit,
                // programPremium should exclude TRIA
                programPremium: locationReviews.reduce((sum, lr) => sum + getBrPremiumSubtotal(lr), 0),
                policyLimitOfLiability: uwBrReview.policyLimitOfLiability,
                lumenLimitOfLiability: uwBrReview.lumenLimitOfLiability,
                followedPolicyNumber: uwBrReview.followedPolicyNumber,
                followedPolicyCoverageForm: uwBrReview.followedPolicyCoverageForm,
                followedPolicyInsurer: uwBrReview.followedPolicyInsurer,
                productLineTypeCode: MY_PRODUCT_LINE_TYPE,
                locationReviews: locationReviews,
                subjectivities: uwSubjectivities.filter((subjectivity) => !!subjectivity.subjectivityText),
                subscriberParticipations: subscribers.map(({ programSubscriberId, isParticipating }) => ({
                    programSubscriberId: programSubscriberId,
                    isParticipating,
                })),
                submissionId: submission.id,
                triaPercentage: exampleTriaPercentage,
                // totalPremium should include TRIA and participation
                totalPremium: totalPremWithParticipationAndTria || 0,
                maximumBrokerCommissionPercentage: uwBrReview.maximumBrokerCommissionPercentage,
            };
        }
        return null;
    };
    // This will build the review payload for the parent component when requested
    useImperativeHandle(ref, () => ({
        buildReviewPayload,
    }));

    // This may be removed in the future. Leaving it for now until we get endorsement code added.
    const seedFoundData = (found: UwReview, isReadOnly: boolean = false) => {
        found.locationReviews.forEach((prop: UwBRLocationReview) => {
            // make sure this is using the most recent numbers since TCV might be updated...
            if (readOnly) {
                // if (isLocationAddEndorsementBool || isLocationRemoveEndorsementBool) {
                //     prop.endorsementPremium = calculateEndorsementPremium(
                //         prop.totalPremium || 0,
                //         endorsingSubmission.effectiveDate,
                //         endorsingSubmission.expirationDate || '',
                //         submission.effectiveDate
                //     );
                // }
                // prop.blendedDoorRate = !!prop.numberUnits ? (prop.totalPremium || 0) / prop.numberUnits : undefined;
            } else {
                // prop.baseRatedPremium = -1; // setting this to negative one will trigger a fetch making sure the base rated premium is always updated as long as we are still underwriting (read only false)
                // prop.totalPremium = -1;
                // prop.blendedDoorRate = undefined;
            }
        });
    };

    const getExistingReview = (isReadOnly: boolean) => {
        if (submission.statusType.code === STATUS_TYPES.READY_TO_QUOTE || isInUnderwriting(submission)) {
            // if we are in these two states and we have a review that hasn't been quoted and is GL line, use that
            let found = uwReviews.find(
                (uwr) =>
                    !uwr.underwriterReview.hasQuote &&
                    uwr.underwriterReview.productLineTypeCode === MY_PRODUCT_LINE_TYPE
            );

            // we still want to "prepopulate" with the most recent review
            if (!found && uwReviews.length > 0) {
                // we want to find the most recent so sort descending
                found = uwReviews
                    .filter((uwr) => uwr.underwriterReview.productLineTypeCode === MY_PRODUCT_LINE_TYPE)
                    .sort((uwr1, uwr2) =>
                        uwr2.underwriterReview.createdAt.localeCompare(uwr1.underwriterReview.createdAt)
                    )[0];

                if (found) {
                    found = deepCopy(found);
                    found.underwriterReview.id = ''; // clear out the IDs to make sure a new review is created;
                    found.locationReviews.forEach((prop: UwBRLocationReview) => {
                        prop.brLocationReviewId = undefined;
                    }); // same for property reviews
                }
            }

            if (found) {
                seedFoundData(found, isReadOnly);
                return found;
            }
        }
        // this is only the case if there aren't any uwReviews at all
        return null;
    };

    const getPropertiesForReview = () =>
        (isLocationAddEndorsementBool
            ? propertyValues.map((p) => ({
                  ...p,
                  // TODO - enable this for BR endorsements when that functionality is added
                  // totalPremium: p.endorsementPremium,
                  // endorsementPremium: undefined,
              }))
            : propertyValues
        ).filter((p) => {
            // make sure this property data is for a property that is valid for the product
            return productFilteredLocations.some((l) => String(l.id) === String(p.locationId));
        });

    const saveUWDraft = async () => {
        const reviewPayload = buildReviewPayload();
        if (reviewPayload === null) {
            setSaveDraftConfirmation('Cannot save: generated payload is null.');
            return;
        }
        const draftPayload = { ...reviewPayload, isDraft: true };

        setSaveDraftRunning(true);

        const newUWReviews = await postUWReview(submission.id, [draftPayload])
            .then((data) => {
                setSaveDraftConfirmation('Draft Saved');
                return data;
            })
            .catch(() => setSaveDraftConfirmation('There was an error saving draft!'));

        setSaveDraftRunning(false);
        if (!!newUWReviews && newUWReviews.length > 0) {
            uwReviewsFetchData(submission.id);
        }
        onSave && onSave();
    };

    const generateFormErrorText = () => {
        const locationReviewErrors = propertyValues.map((prop) => {
            return validateLocationReview(prop);
        });
        const combinedSubscriberCapacity = getCombinedSubscriberCapacity(subscribers);
        const uwReviewErrors: { [field: string]: string } = validateBrUnderwriterReview(
            uwBrReview,
            combinedSubscriberCapacity
        );
        let errorText = '';
        locationReviewErrors.forEach((errors, index) => {
            if (Object.keys(errors).length > 0) {
                errorText += `${index > 0 ? '\n\n' : ''}Errors for Location ${index + 1}:\n`;
                Object.entries(errors).forEach(([field, message]) => {
                    errorText += ` - ${formatCamelCase(message)}`;
                });
            }
        });
        if (Object.keys(uwReviewErrors).length > 0) {
            errorText += `\n\nErrors for Underwriter Review:`;
            Object.entries(uwReviewErrors).forEach(([field, message]) => {
                errorText += ` - ${formatCamelCase(message)}\n`;
            });
        }
        return setFormErrorText(errorText);
    };

    const formatCamelCase = (text: string) => {
        return text
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/([a-zA-Z])([0-9]+)/g, '$1 $2')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    // Call out to parent to let it know if our review is valid or not
    useEffect(() => {
        onReviewValid && onReviewValid(MY_PRODUCT_LINE_TYPE, isReviewValid, productFilteredLocations.length > 0);
    }, [isReviewValid, uwBrReview]);

    // When property values change, validate this review
    useEffect(() => {
        if (!propertyValues.length) {
            setIsReviewValid(false);
            return;
        }

        const combinedSubscriberCapacity = getCombinedSubscriberCapacity(subscribers);

        // No errors on location, no errors on top level review, and we have an ID (meaning it's not a new unsaved review)
        setIsReviewValid(
            propertyValues.every((prop) => {
                const errors = validateLocationReview(prop);
                // Return true if no keys in errors
                return Object.keys(errors).length === 0;
            }) &&
                Object.keys(validateBrUnderwriterReview(uwBrReview, combinedSubscriberCapacity)).length === 0 &&
                !!uwBrReview.id
        );

        generateFormErrorText();
    }, [propertyValues, uwBrReview, subscribers]);

    return (
        <>
            <Grid container direction="column" spacing={2}>
                <Grid item xs={12}>
                    <Grid container spacing={2} direction="row">
                        <Grid item xs={6}>
                            <ProjectDescription />
                        </Grid>
                        <Grid item xs={6}>
                            <UnderwriterNotes />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <TopLevelReviewCard readOnly={readOnly}></TopLevelReviewCard>
                </Grid>
                <Grid item xs={12}>
                    <BrLocationReviewGrid readOnly={readOnly}></BrLocationReviewGrid>
                </Grid>
                {!readOnly && (
                    <Grid item xs={12} style={{ marginTop: '-1rem' }}>
                        <Grid container spacing={4} alignItems="center">
                            {showSaveDraftButton && (
                                <Grid item>
                                    <LrButton
                                        lrType="secondary"
                                        isLoading={saveDraftRunning}
                                        onClick={() => saveUWDraft()}
                                        style={{ marginLeft: '0.5rem' }}
                                    >
                                        Save Draft
                                    </LrButton>
                                </Grid>
                            )}
                            {formErrorText && formErrorText !== '' && (
                                <>
                                    <Grid item>
                                        <LrButton
                                            lrType="text"
                                            onClick={() => {
                                                setIsShowingErrorText(!isShowingErrorText);
                                            }}
                                        >
                                            {isShowingErrorText ? 'Hide ' : 'Show '} Problems
                                        </LrButton>
                                    </Grid>
                                    <Grid item>
                                        {isShowingErrorText && (
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                                {formErrorText}
                                            </Typography>
                                        )}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                )}
            </Grid>

            <Spacing height="24px" />
            <LrToastNotify open={!!saveDraftConfirmation} onClose={() => setSaveDraftConfirmation('')}>
                {saveDraftConfirmation}
            </LrToastNotify>
        </>
    );
};

const ForwardedBuildersRiskStandardReview = forwardRef(BuildersRiskStandardReview);
export default React.memo(ForwardedBuildersRiskStandardReview);
